import {
  DOME_SUBTYPE,
  EAR_MOLD_SUBTYPE,
  RECEIVER_SUBTYPE,
  TECHNOLOGIES,
  TUBE_SUBTYPE,
} from "../constants/products";
import {
  ACCESSORY_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
} from "../constants/sales";
import {
  DomeInventoryProductSpecification,
  EarmoldInventoryProductSpecification,
  HearingAidInventoryProductSpecification,
  ReceiverInventoryProductSpecification,
  TubeInventoryProductSpecification,
} from "../services/types/inventory-products.type";
import { HearingAidMasterProductSpecification } from "../services/types/master-products.type";
import { Order } from "services/types/orders.type";
import { formatBackendDateOnly } from "./common";
import { formatPatient } from "./patients.util";
import { getEarText } from "features/inventory/orders/orders.util";

export const formatOrder = (order: Order) => {
  return {
    ...order,
    patient: order.patient && formatPatient(order.patient),
  };
};

export const getInventoryProductDetails = ({
  ear,
  specification,
  manufacturer_warranty_expiration_date,
  loss_damage_warranty_expiration_date,
  product: {
    master_product: {
      specification: masterProductSpecification,
      type,
      subtype,
    },
  },
}: any) => {
  const specificationDetails: string[] = [];
  if (ear) {
    specificationDetails.push(`Ear: ${getEarText(ear)}`);
  }
  if (type === HEARING_AID_PRODUCT_TYPE) {
    const { technology } =
      (masterProductSpecification as HearingAidMasterProductSpecification) ??
      {};
    const { color, battery, manufacturer_instruction, additional_notes } =
      (specification as HearingAidInventoryProductSpecification) ?? {};
    specificationDetails.push(
      ...[
        technology ? `Technology: ${TECHNOLOGIES.getFromCode(technology)}` : "",
        color ? `Color: ${color}` : "",
        battery ? `Battery: ${battery}` : "",
        manufacturer_instruction
          ? `Manufacturer Instruction: ${manufacturer_instruction}`
          : "",
        additional_notes ? `Additional notes: ${additional_notes}` : "",
      ]
    );
  } else if (type === ACCESSORY_PRODUCT_TYPE && subtype === RECEIVER_SUBTYPE) {
    const { length, gain, additional_notes } =
      (specification as ReceiverInventoryProductSpecification) ?? {};
    specificationDetails.push(
      ...[
        gain ? `Gain: ${gain}` : "",
        length || typeof length === "number" ? `Length: ${length}` : "",
        additional_notes ? `Additional notes: ${additional_notes}` : "",
      ]
    );
  } else if (type === ACCESSORY_PRODUCT_TYPE && subtype === EAR_MOLD_SUBTYPE) {
    const { options, venting, material, canal_length, additional_notes } =
      (specification as EarmoldInventoryProductSpecification) ?? {};
    specificationDetails.push(
      ...[
        canal_length ? `Canal Length: ${canal_length}` : "",
        venting ? `Venting: ${venting}` : "",
        material ? `Material: ${material}` : "",
        options ? `Options: ${options}` : "",
        additional_notes ? `Additional notes: ${additional_notes}` : "",
      ]
    );
  } else if (type === ACCESSORY_PRODUCT_TYPE && subtype === DOME_SUBTYPE) {
    const {
      additional_notes = "",
      type,
      size,
    } = (specification as DomeInventoryProductSpecification) ?? {};
    specificationDetails.push(
      ...[
        type ? `Type: ${type}` : "",
        size ? `Size: ${size}` : "",
        additional_notes ? `Additional notes: ${additional_notes}` : "",
      ]
    );
  } else if (type === ACCESSORY_PRODUCT_TYPE && subtype === TUBE_SUBTYPE) {
    const { additional_notes = "", size } =
      (specification as TubeInventoryProductSpecification) ?? {};
    specificationDetails.push(
      ...[
        size ? `Size: ${size}` : "",
        additional_notes ? `Additional notes: ${additional_notes}` : "",
      ]
    );
  }
  specificationDetails.push(
    ...[
      manufacturer_warranty_expiration_date
        ? `MFR Repair Warranty: ${formatBackendDateOnly(
            manufacturer_warranty_expiration_date,
            "MMM dd, yyyy"
          )}`
        : "",
      loss_damage_warranty_expiration_date
        ? `L&D Warranty: ${formatBackendDateOnly(
            loss_damage_warranty_expiration_date,
            "MMM dd, yyyy"
          )}`
        : "",
    ]
  );

  return specificationDetails.filter(Boolean);
};
